import { useEffect, useState } from 'react';
import { useIOSPresentation } from '../lib/web-presentation';
import { presentAppRecordCopy } from '../features/shared/record-copy';
import { bindPersonalTaxFacts, filingDeadlineNoticesForSubject, formatKobo, koboToNaira as savedKoboToNaira } from '../features/return-centre';
import { proposedPersonalIntakeCategory, TrackB2Journey, trackB2Fixture,
  type PersonalTaxFactsInput, type TrackB2Model } from '../features/track-b2';
import { applyEvidenceDuplicatePresentation, applyEvidenceProposalBlockers, applyEvidenceReviewProgress, applyExtractedOwnership, detectEvidenceOwner, evaluateExtractedOwnership, reconciliationFromResult,
  mergeRecoveredEvidence, restorePersistedEvidence, settleEvidence, settleEvidenceWorkflowError,
  purgeEvidenceUsage, type SharedJourneyPorts } from '../features/shared';
import { ApiError, fileToBase64, type TaxCalculationResult, type TaxReturnResource } from '../lib/api';
import type { PersonaWorkspaceProps } from '../types/persona';
import { WorkspaceContextHeader } from '../components';
import { PersonalTaxYearDialog } from '../components/FirstUseSetup';
import { personalTaxTraceLabel, usePersonalFilingWorkspace } from '../features/personal-filing/personal-filing';
import { classificationSourceUnavailable } from '../features/shared/evidence-results';

export function IndividualWorkspace({ api, subjects, onContextsChanged }: PersonaWorkspaceProps) {
  const individual = subjects.find((subject) => subject.kind === 'individual');
  const yearContexts = (individual?.resolved_contexts ?? []).filter((context) => context.scope.rail === 'tax_return')
    .sort((left, right) => right.scope.periodId.localeCompare(left.scope.periodId));
  const years = [...new Set(yearContexts.map((context) => Number(context.scope.periodId)).filter(Number.isSafeInteger))];
  const [selectedYear, setSelectedYear] = useState(years[0] ?? new Date().getUTCFullYear());
  const [addingYear, setAddingYear] = useState(false);
  const selectedContext = yearContexts.find((context) => Number(context.scope.periodId) === selectedYear) ?? yearContexts[0];
  const selectYear = (year: number) => {
    const context = yearContexts.find((candidate) => Number(candidate.scope.periodId) === year);
    if (!context) return; api.selectContext(context); setSelectedYear(year);
  };
  const selectedSubject = individual && selectedContext ? { ...individual, engagement_id: selectedContext.scope.engagementId,
    context_id: selectedContext.contextId, context_ids: { ...individual.context_ids, tax_return: selectedContext.contextId },
    resolved_contexts: [selectedContext] } : individual;
  return <><WorkspaceContextHeader actionAccessibleLabel="Start another tax year" actionLabel="Tax year"
    contextLabel="Taxpayer" contextValue={individual?.display_name ?? 'Personal taxpayer'}
    onAction={() => setAddingYear(true)} onPeriodChange={(value) => selectYear(Number(value))}
    periodLabel="Tax year" periodOptions={years.map((year) => ({ label: String(year), value: String(year) }))}
    periodValue={String(selectedYear)} />
    {selectedSubject && <IndividualTaxWorkspace key={`${selectedSubject.id}:${selectedYear}`} api={api}
      subjects={[selectedSubject]} taxYear={selectedYear} />}
    {addingYear && <PersonalTaxYearDialog api={api} existingYears={years} onCancel={() => setAddingYear(false)}
      onComplete={() => { setAddingYear(false); onContextsChanged?.(); }} />}</>;
}

function IndividualTaxWorkspace({ api, subjects, taxYear }: PersonaWorkspaceProps & { taxYear: number }) {
  const ios = useIOSPresentation();
  const individual = subjects.find((subject) => subject.kind === 'individual');
  const [model, setModel] = useState<TrackB2Model>(() => buildInitialPersonalModel(individual, taxYear));
  const [notice, setNotice] = useState('');
  const [taxReturn, setTaxReturn] = useState<TaxReturnResource>();
  const periodStart = `${taxYear}-01-01`;
  const periodEndExclusive = `${taxYear + 1}-01-01`;
  const evidenceContext = individual?.resolved_contexts?.find((candidate) => candidate.scope.rail === 'tax_return');
  const personalFiling = usePersonalFilingWorkspace({ api, contextId: evidenceContext?.contextId,
    taxpayerName: individual?.display_name ?? 'Personal taxpayer', taxYear, calculationId: model.calculation?.id,
    calculation: model.calculation });
  useEffect(() => {
    if (!individual || !evidenceContext || typeof api.listClassificationJobs !== 'function') return;
    const controller = new AbortController();
    api.selectContext(evidenceContext);
    void restorePersistedEvidence(api, evidenceContext, { id: individual.id, name: individual.display_name,
      addedBy: 'Individual taxpayer' }, (recovered) => setModel((value) => mergeRecoveredEvidence(value, recovered)),
    controller.signal).catch((error: unknown) => {
      if (controller.signal.aborted || error instanceof DOMException && error.name === 'AbortError') return;
      setNotice(error instanceof Error ? `Saved documents could not be restored: ${error.message}`
        : 'Saved documents could not be restored.');
    });
    return () => controller.abort();
  }, [api, evidenceContext?.contextId, individual?.display_name, individual?.id]);
  useEffect(() => {
    if (!individual || typeof api.loadPersonalTaxDraft !== 'function') return;
    const context = individual.resolved_contexts?.find((candidate) => candidate.scope.rail === 'tax_return');
    if (context) api.selectContext(context);
    const controller = new AbortController();
    api.loadPersonalTaxDraft(periodStart, periodEndExclusive, controller.signal).then(async (draft) => {
      const packageId = context?.scope.packageId;
      if (packageId !== 'AMOYE-PIT-ANNUAL-NRS-SPECIAL-CAND-001'
        && packageId !== 'AMOYE-PIT-ANNUAL-STATE-CAND-001') return;
      if (draft.state === 'SAVED') {
        const taxFacts = personalTaxInputFromDraft(packageId, draft.facts);
        setModel((value) => draft.calculation
          ? withPersonalTaxCalculation(value, taxFacts, draft.facts, draft.calculation)
          : { ...value, taxFacts });
      }
      if (typeof api.listTaxReturns !== 'function') return;
      const { taxReturns } = await api.listTaxReturns(individual.id, controller.signal);
      const current = taxReturns.find((candidate) => candidate.track === 'B2'
        && candidate.taxpayerId === individual.id && candidate.packageId === packageId
        && candidate.periodStart === periodStart && candidate.periodEndExclusive === periodEndExclusive);
      if (current) {
        setTaxReturn(current);
        setModel((value) => withPersonalReturn(value, current));
      }
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setNotice(error instanceof Error ? `Your saved tax entries could not be loaded: ${error.message}`
        : 'Your saved tax entries could not be loaded.');
    });
    return () => controller.abort();
  }, [api, individual?.id, periodEndExclusive, periodStart]);

  const persistTaxPositionDraft = async (facts: PersonalTaxFactsInput) => {
    if (typeof api.savePersonalTaxDraft !== 'function') return;
    const packageContext = individual?.resolved_contexts?.find((context) => context.scope.rail === 'tax_return'
      && context.scope.packageId === facts.packageId);
    if (packageContext) api.selectContext(packageContext);
    const { engineFacts } = await bindPersonalTaxFacts(facts, periodStart);
    await api.savePersonalTaxDraft({ periodStart, periodEndExclusive, facts: engineFacts });
    setModel((value) => {
      if (samePersonalTaxFacts(value.taxFacts, facts)) return value;
      const { effectiveRateLabel: _effectiveRateLabel, ...taxPosition } = value.taxPosition;
      return { ...value, taxFacts: facts, calculation: undefined,
        taxPosition: { ...taxPosition, incomeLabel: 'Awaiting calculation',
          deductionsLabel: 'Awaiting calculation', estimatedPositionLabel: 'Not calculated',
          status: 'incomplete', missingItems: ['Recalculate after changing your saved amounts'] },
        returnDraft: { ...value.returnDraft, id: 'personal-return-not-created', version: 'Not created',
          state: 'not-ready', declarationVersion: 'Not selected',
          blockers: ['Recalculate after changing your saved amounts'] } };
    });
  };
  const finishEvidenceProcessing = async (file: File, queuedJobId: string) => {
    if (!individual) throw new Error('No individual tax subject is available for this identity.');
    const job = await api.waitForJob(queuedJobId, { contextId: evidenceContext?.contextId });
    if (job.status === 'failed') {
      if (classificationSourceUnavailable(job)) {
        setModel((value) => ({ ...value, evidence: value.evidence.map((item) => item.id === queuedJobId
          ? settleEvidence(item, job) : item) }));
        setNotice('The stored source is unavailable for review. Check its details for recovery options; manual tax amounts remain available.');
        return;
      }
      const promoted = await api.prepareClassificationEvidenceForHumanReview(file, queuedJobId, undefined, evidenceContext);
      setModel((value) => ({ ...value, evidence: value.evidence.map((item) => item.id === queuedJobId
        ? { ...settleEvidence(item, job), serverEvidenceId: promoted.evidenceId,
          ...(promoted.duplicateOfEvidenceId ? { duplicateOfEvidenceId: promoted.duplicateOfEvidenceId } : {}),
          issue: `${settleEvidence(item, job).issue ?? ''} Review the source and classify it manually if Amoye’s assessment is not correct.`.trim() }
        : item) }));
      setNotice('Amoye could not create reliable proposals. The secured source is available for your review and manual classification.'); return;
    }
    const result = await api.result(queuedJobId, undefined, evidenceContext?.contextId);
    const promoted = await api.promoteClassificationEvidence(file, queuedJobId, undefined, evidenceContext);
    const extractedOwnership = evaluateExtractedOwnership(result.document_analysis,
      { id: individual.id, name: individual.display_name });
    setModel((value) => {
      const current = value.evidence.find((item) => item.id === queuedJobId);
      const ownership = current ? applyExtractedOwnership(settleEvidence(current, job), result,
        { id: individual.id, name: individual.display_name }) : undefined;
      const proposals = reconciliationFromResult(result, file.name, queuedJobId).map((proposal) => {
        const serverProposals = (promoted.proposalsByItemId?.[proposal.id] ?? [])
          .filter((candidate) => !candidate.fieldPath.endsWith('.ownership_state'));
        const linked = { ...proposal, serverProposalIds: serverProposals.length
          ? serverProposals.map((candidate) => candidate.id)
          : promoted.proposalIdsByTransactionId[proposal.id] ?? [], serverProposals };
        return applyEvidenceProposalBlockers(linked, ownership?.evidence, promoted.duplicateOfEvidenceId);
      });
      const documentSection = personalSectionForDocumentCategory(result.document_analysis?.document_category);
      return { ...value,
        evidence: value.evidence.map((item) => item.id === queuedJobId
          ? { ...applyEvidenceDuplicatePresentation(ownership?.evidence ?? settleEvidence(item, job), promoted.duplicateOfEvidenceId), serverEvidenceId: promoted.evidenceId,
            ...(promoted.processingGrantId ? { processingGrantId: promoted.processingGrantId } : {}),
            ...((promoted.proposalsByItemId?.[queuedJobId] ?? []).some((proposal) => proposal.fieldPath.endsWith('.ownership_state'))
              ? { serverOwnershipProposals: (promoted.proposalsByItemId?.[queuedJobId] ?? [])
                .filter((proposal) => proposal.fieldPath.endsWith('.ownership_state')) } : {}) } : item),
        reconciliation: [...proposals, ...value.reconciliation.filter((item) => item.evidenceId !== queuedJobId)],
        intake: value.intake.map((section) => (documentSection === section.id
          || proposals.some((item) => proposedPersonalIntakeCategory(item) === section.id))
          ? { ...section, state: ownership?.blockProposals ? 'needs-attention' : section.state === 'complete' ? 'complete' : 'in-progress',
            sourceIds: [...new Set([...section.sourceIds, queuedJobId])] }
          : section) };
    });
    setNotice(extractedOwnership?.state === 'mismatch'
      ? 'A possible taxpayer mismatch needs review. View the document, then confirm or flag its ownership.'
      : `${result.transactions.length} source-derived proposal${result.transactions.length === 1 ? '' : 's'} ready for human review.`);
  };
  const openOrCreateReturnDraft = async () => {
    if (personalFiling.localEnabled) { await personalFiling.prepare(); return; }
    if (!individual || !model.calculation) {
      setNotice('Complete and calculate your tax position before creating the return.'); return;
    }
    const packageId = model.taxFacts.packageId;
    const packageContext = individual.resolved_contexts?.find((context) => context.scope.rail === 'tax_return'
      && context.scope.packageId === packageId);
    if (!packageContext) {
      setNotice('This filing jurisdiction is not available for the selected taxpayer and tax year.'); return;
    }
    api.selectContext(packageContext);
    const findExisting = async () => {
      const { taxReturns } = await api.listTaxReturns(individual.id);
      return taxReturns.find((candidate) => candidate.track === 'B2' && candidate.taxpayerId === individual.id
        && candidate.packageId === packageId && candidate.periodStart === periodStart
        && candidate.periodEndExclusive === periodEndExclusive);
    };
    try {
      let current = await findExisting();
      if (!current) {
        try {
          current = await api.createPersonalReturn({ taxpayerId: individual.id, packageId,
            periodStart, periodEndExclusive });
        } catch (error) {
          if (!(error instanceof ApiError) || error.status !== 409) throw error;
          current = await findExisting();
          if (!current) throw error;
        }
      }
      if (['DRAFT', 'FACTS_ATTACHED', 'CALCULATED'].includes(current.status)
        && current.factSnapshotSha256 !== model.calculation.inputSnapshotSha256) {
        current = await api.attachReturnFacts(current.id, model.calculation.inputSnapshotSha256);
      }
      if (model.calculation.acceptance === 'PRACTITIONER_ACCEPTED'
        && ['DRAFT', 'FACTS_ATTACHED', 'CALCULATED'].includes(current.status)
        && current.calculationSha256 !== model.calculation.outputSha256) {
        current = await api.attachReturnCalculation(current.id, { calculationId: model.calculation.id });
      }
      setTaxReturn(current);
      setModel((value) => withPersonalReturn(value, current!));
      setNotice('Your return draft and figures are saved. Continue from the available preparation step.');
    } catch (error) {
      setNotice(error instanceof Error ? error.message : 'The personal return could not be created.');
    }
  };
  const shared: SharedJourneyPorts = {
    switchContext: () => undefined,
    loadEvidenceActivity: async (limit, signal) => {
      if (!individual || !evidenceContext) return [];
      return (await api.evidenceActivity(individual.id, limit, signal, evidenceContext.contextId)).events;
    },
    registerEvidence: async (file, _context, options) => {
      if (!individual) { setNotice('No individual tax subject is available for this identity.'); return; }
      const ownership = await detectEvidenceOwner(file, { id: individual.id, name: individual.display_name });
      let queuedJobId: string | undefined;
      try {
        const response = await api.upload({ client_id: individual.id, filename: file.name,
          mime_type: file.type || inferMimeType(file.name), content_base64: await fileToBase64(file),
          ...(options?.modelAssistanceRequested ? { model_assistance_requested: true } : {}) }, options?.signal);
        queuedJobId = response.result.queued_job_id;
        setModel((value) => ({ ...value,
          evidence: [...value.evidence, { id: response.result.queued_job_id, name: file.name, sourceType: evidenceSource(file.name),
            state: 'checking', addedBy: 'Individual taxpayer', addedAt: 'Just now', hashLabel: 'Integrity check recorded', documentAvailable: true,
            taxpayerId: individual.id, taxpayerName: individual.display_name, ownershipState: ownership.state,
            ...(ownership.detectedName ? { ownershipProposal: { detectedName: ownership.detectedName,
              basis: ownership.detectedBy ?? 'browser-source-inspection', extractor: 'browser-source-inspection/2' } } : {}),
            ...(ownership.state === 'mismatch' ? { issue: `Possible wrong taxpayer: the source identifies ${ownership.detectedName}. Its proposals are blocked until ownership is reviewed.` } : {}) }] }));
        setNotice(presentAppRecordCopy(ownership.state === 'mismatch' ? 'Evidence secured in ownership review. Processing is running, but its proposals will remain blocked.'
          : 'Evidence secured. Format-appropriate source checks and classification are running.', ios));
        options?.onAccepted?.();
        await finishEvidenceProcessing(file, queuedJobId);
      } catch (error) {
        if (queuedJobId) setModel((value) => ({ ...value, evidence: value.evidence.map((item) => item.id === queuedJobId
          ? settleEvidenceWorkflowError(item, error) : item) }));
        setNotice(error instanceof Error ? error.message : presentAppRecordCopy('Evidence registration failed.', ios));
        if (!queuedJobId) throw error;
      }
    },
    retryEvidenceProcessing: async (item) => {
      setModel((value) => ({ ...value, evidence: value.evidence.map((candidate) => candidate.id === item.id
        ? { ...candidate, state: 'checking', issue: 'Resuming from the secured source…' } : candidate) }));
      try {
        const source = await api.evidenceDocument(item.id);
        const file = new File([source.blob], source.filename || item.name, { type: source.mimeType });
        await finishEvidenceProcessing(file, item.id);
      } catch (error) {
        setModel((value) => ({ ...value, evidence: value.evidence.map((candidate) => candidate.id === item.id
          ? settleEvidenceWorkflowError(candidate, error) : candidate) }));
        setNotice(error instanceof Error ? error.message : 'Processing could not be resumed.');
        throw error;
      }
    },
    reviewReconciliation: async (item, decision, correction) => {
      if (decision === 'correct') {
        if (!item.serverProposals?.length || !correction) throw new Error('Enter and save a correction before continuing.');
        await api.correctEvidenceProposals(item.serverProposals, correction);
      } else {
        if (!item.serverProposalIds?.length) throw new Error('This item is not linked to a durable review record.');
        await api.reviewEvidenceProposals(item.serverProposalIds, decision);
      }
      setModel((value) => {
      const reconciliation = value.reconciliation.map((entry) => entry.id === item.id
        ? { ...entry, ...(correction?.ntaCode ? { ntaCode: correction.ntaCode } : {}),
          ...(correction?.vatAmountKobo ? { vatAmountKobo: correction.vatAmountKobo } : {}),
          ...(correction?.evidenceCategory ? { evidenceCategory: correction.evidenceCategory } : {}),
          state: decision === 'correct' ? 'corrected-by-human' as const
          : decision === 'accept' ? 'accepted-by-human' as const
            : decision === 'exclude' ? 'excluded-by-human' as const : 'blocked' as const }
        : entry);
      const taxFacts = reviewedPersonalTaxFacts(value.taxFacts, reconciliation);
      const intake: TrackB2Model['intake'] = value.intake.map((section) => {
        const categoryItems = reconciliation.filter((entry) => proposedPersonalIntakeCategory(entry) === section.id);
        if (categoryItems.length === 0) return section;
        const accepted = categoryItems.filter((entry) => ['accepted-by-human', 'corrected-by-human'].includes(entry.state));
        const missingItems = section.missingItems.filter((missing) => !accepted.some((entry) =>
          missingItemSatisfied(missing, entry.ntaCode)));
        return { ...section, missingItems, state: categoryItems.every((entry) =>
          ['accepted-by-human', 'corrected-by-human'].includes(entry.state))
          ? 'complete' : categoryItems.some((entry) => ['blocked', 'corrected-by-human', 'excluded-by-human'].includes(entry.state))
            ? 'needs-attention' : 'in-progress' };
      });
      const managedRequirements = new Set(value.intake.flatMap((section) => section.missingItems));
      const remainingRequirements = intake.flatMap((section) => section.missingItems);
      return { ...value, reconciliation, evidence: applyEvidenceReviewProgress(value.evidence, reconciliation, item.evidenceId), taxFacts, intake,
        taxPosition: { ...value.taxPosition, missingItems: [...new Set([
          ...value.taxPosition.missingItems.filter((requirement) => !managedRequirements.has(requirement)),
          ...remainingRequirements,
        ])] } };
      });
    },
    unlinkEvidence: async (item, input) => {
      if (item.processingGrantId && item.serverEvidenceId) {
        await api.revokeEvidenceProcessing(item.processingGrantId, item.serverEvidenceId);
      }
      await api.deleteClassificationEvidence(item.id, input.reason, input.batchId);
      setTaxReturn(undefined);
      setModel((value) => {
        const purged = purgeEvidenceUsage(value, item.id);
        const reconciliation = [...purged.reconciliation];
        return { ...purged, reconciliation, calculation: undefined,
          taxFacts: reviewedPersonalTaxFacts(value.taxFacts, reconciliation),
          intake: purged.intake.map((section) => ({ ...section,
            sourceIds: section.sourceIds.filter((evidenceId) => evidenceId !== item.id),
            state: section.sourceIds.includes(item.id) ? 'needs-attention' : section.state })),
          taxPosition: { ...purged.taxPosition, status: 'incomplete',
            missingItems: [...new Set([...purged.taxPosition.missingItems, 'Review replacement evidence after deleting a source'])] },
          returnDraft: { ...purged.returnDraft, state: 'not-ready', blockers: ['Recalculate after reviewing replacement evidence'] } };
      });
      setNotice(`${item.name} was permanently deleted. Its stored file and derived proposals will not return after sign-in.`);
    },
    viewEvidence: async (item) => api.evidenceDocument(item.id),
    reviewEvidenceOwnership: async (item, decision) => {
      if (!item.serverOwnershipProposals?.length) throw new Error(item.duplicateOfEvidenceId
        ? 'This copy duplicates an earlier secured source. Resolve ownership on the original source.'
        : 'Ownership review is not ready yet. Resume processing before recording this decision.');
      await api.reviewEvidenceOwnershipProposals(item.serverOwnershipProposals, decision);
      setTaxReturn(undefined);
      if (decision === 'flag-wrong-taxpayer') {
        if (item.processingGrantId && item.serverEvidenceId) {
          await api.revokeEvidenceProcessing(item.processingGrantId, item.serverEvidenceId);
        }
        await api.deleteClassificationEvidence(item.id, 'wrong-taxpayer');
        setModel((value) => purgeEvidenceUsage(value, item.id));
        setNotice(`${item.name} was flagged as belonging to the wrong taxpayer and permanently deleted.`); return;
      }
      setModel((value) => ({ ...value,
        evidence: value.evidence.map((entry) => entry.id === item.id ? { ...entry, state: 'ready-for-review',
          ownershipState: 'confirmed', issue: 'Ownership confirmed. Review the extracted proposals next.' } : entry),
        reconciliation: value.reconciliation.map((entry) => entry.evidenceId === item.id && entry.state === 'blocked'
          ? { ...entry, state: 'needs-review', dispositionReason: undefined } : entry),
        intake: value.intake.map((section) => section.sourceIds.includes(item.id) ? { ...section, state: 'in-progress' } : section) }));
      setNotice(`Ownership confirmed for ${item.name}. Its proposals are ready for review.`);
    },
    humanClassifyEvidence: async (item, input) => {
      if (!item.serverEvidenceId) throw new Error('The secured source is not ready for a recorded human decision yet.');
      await api.humanClassifyEvidence(item.serverEvidenceId, { ...input, reviewedAt: new Date().toISOString() });
      setTaxReturn(undefined);
      const sectionId = personalSectionForDocumentCategory(input.evidenceCategory);
      setModel((value) => {
        const reconciliation = value.reconciliation.map((entry) => entry.evidenceId === item.id && entry.state === 'blocked'
          && entry.periodState !== 'mismatch' && !item.duplicateOfEvidenceId
          ? { ...entry, state: 'needs-review' as const, dispositionReason: undefined } : entry);
        const evidence = value.evidence.map((entry) => entry.id === item.id ? { ...entry,
          state: entry.periodState === 'mismatch' ? 'blocked' as const : 'reviewed' as const,
          ownershipState: 'confirmed' as const, classificationCategory: input.evidenceCategory,
          issue: entry.periodState === 'mismatch'
            ? `Category recorded as ${input.evidenceCategory.replaceAll('_', ' ')}, but this source is still outside the selected filing period. Move it to the correct period or remove it from this return.`
            : `Human classification recorded: ${input.evidenceCategory.replaceAll('_', ' ')}. The automated assessment remains in the audit trail.` }
          : entry);
        return { ...value, evidence: applyEvidenceReviewProgress(evidence, reconciliation, item.id), reconciliation,
        intake: value.intake.map((section) => sectionId === section.id ? { ...section,
          sourceIds: [...new Set([...section.sourceIds, item.id])],
          state: section.state === 'complete' ? 'complete' : 'in-progress' } : section),
        calculation: undefined,
        returnDraft: { ...value.returnDraft, state: 'not-ready', blockers: ['Recalculate after reviewing evidence'] } };
      });
      setNotice(`${item.name} is now linked as ${input.evidenceCategory.replaceAll('_', ' ')} based on your recorded review.`);
    },
    performResponsibilityAction: async (action) => {
      if (!taxReturn) { setNotice('Calculate the tax position before starting return actions.'); return; }
      if (action === 'professional-review') {
        setNotice('Professional matching and cross-account invitations are not connected yet. If you already have a practitioner, contact them outside Amoye for now. Do not send records through Amoye until a scoped engagement is authorised.'); return;
      }
      if (action === 'request-mock-submission') {
        setNotice('Filing is not available yet. Your prepared return remains saved and you can continue reviewing it.'); return;
      }
      try {
        const result = action === 'declare-sign'
          ? await api.signReturn(taxReturn.id, 'personal-declaration-v1', 'individual-taxpayer')
          : action === 'prepare'
              ? await api.mutateReturn(taxReturn.id, 'prepare', 'individual-taxpayer')
              : undefined;
        if (!result) { setNotice('This action is unavailable in the individual context.'); return; }
        setTaxReturn(result);
        setModel((value) => ({ ...value,
          responsibilities: value.responsibilities.map((step) => step.action === action ? { ...step, state: 'complete' }
            : action === 'prepare' && step.action === 'declare-sign' ? { ...step, state: 'ready' } : step),
          returnDraft: { ...value.returnDraft,
            state: result.status === 'SIGNED' || result.status === 'SUBMISSION_REQUESTED' ? 'signed-for-mock-request' : value.returnDraft.state },
          submission: result.status === 'SUBMISSION_REQUESTED' ? [...value.submission, { id: result.submissionRequestId ?? result.id,
            label: 'Mock filing request created', owner: 'Amoye', state: 'current', occurredAt: new Date().toISOString(),
            detail: 'Prepared for a local delivery test. This is not an NRS acceptance or filing outcome.' }] : value.submission }));
        setNotice(`Return action completed: ${action.replaceAll('-', ' ')}. Current state: ${result.status}.`);
      } catch (error) { setNotice(error instanceof Error ? error.message : 'Return action failed.'); }
    },
    reconcileBridge: () => setModel((value) => ({ ...value, bridge: { ...value.bridge,
      reconciliationRequired: false, retryAllowed: true, detail: 'The personal-return delivery test was checked. No authority outcome was created.' } })),
    retryBridge: () => setNotice('The unchanged personal return was queued again for a delivery test.'),
    setIntelligenceEnabled: (enabled) => setModel((value) => ({ ...value,
      intelligence: { ...value.intelligence, enabled, status: enabled ? 'ready' : 'off' } })),
    loadHomeSuggestions: signal => api.loadHomeSuggestions({ rail: 'tax_return' }, signal),
    queryIntelligence: async (prompt, consentEvidenceId, signal, historyMode = 'EPHEMERAL') => {
      const response = await api.queryAssistantIntelligence({ rail: 'tax_return', prompt,
        consentEvidenceId, historyMode }, signal);
      return { answer: response.answer, citations: response.citations.map(({
        sourceId, sourceVersion, locator, title, authority, sourceUri,
      }) => ({ sourceId, sourceVersion, locator, title, authority, sourceUri })),
        limitations: response.limitations, generatedAt: response.generatedAt,
        modelId: response.modelId, modelVersion: response.modelVersion,
        activities: response.activities, navigationIntents: response.navigationIntents,
        historyMode: response.conversation.historyMode, retainedUntil: response.conversation.retainedUntil };
    },
    loadIntelligenceHistory: async (consentEvidenceId, signal) => (await api.loadAssistantHistory({
      rail: 'tax_return', consentEvidenceId }, signal)).turns.map((turn) => ({ ...turn,
        response: { answer: turn.response.answer,
          citations: turn.response.citations.map(({ sourceId, sourceVersion, locator, title, authority, sourceUri }) =>
            ({ sourceId, sourceVersion, locator, title, authority, sourceUri })),
          limitations: turn.response.limitations, generatedAt: turn.response.generatedAt,
          modelId: turn.response.modelId, modelVersion: turn.response.modelVersion,
          activities: turn.response.activities, navigationIntents: turn.response.navigationIntents,
          historyMode: turn.response.conversation.historyMode,
          retainedUntil: turn.response.conversation.retainedUntil } })),
    deleteIntelligenceHistory: (consentEvidenceId, signal) => api.deleteAssistantHistory({
      rail: 'tax_return', consentEvidenceId }, signal),
  };
  return <TrackB2Journey model={personalFiling.authorityLabel ? { ...model,
    taxPosition: { ...model.taxPosition, jurisdictionLabel: personalFiling.authorityLabel } } : model} notice={notice}
    conversationScope={`${individual?.id ?? model.context.id}:${taxYear}:${individual?.context_ids?.tax_return ?? individual?.context_id ?? 'unresolved'}`} slots={{
    filingProfile: personalFiling.profilePanel,
    ...(personalFiling.filing?.preparation ? { returnStatus: !personalFiling.filing.currentInputsMatch
      ? 'Update prepared return'
      : personalFiling.filing.portalReports?.some((report) => report.preparationId === personalFiling.filing?.preparation?.id
        && report.artifactSha256 === personalFiling.filing?.preparation?.artifactSha256)
        ? 'Submission recorded'
        : personalFiling.filing.preparation.status === 'SIGNED' ? 'Signed' : 'Prepared' } : {}),
    ...(personalFiling.localEnabled ? { returnPreparation: personalFiling.returnPanel, filing: personalFiling.filingPanel } : {}),
  }} ports={{
    ...shared,
    openIntakeSection: (sectionId) => {
      setModel((value) => ({ ...value, intake: value.intake.map((section) => section.id === sectionId
        ? { ...section, state: section.state === 'complete' ? 'complete' : 'in-progress' } : section) }));
      setNotice(`Opened ${sectionId} evidence and fact intake.`);
    },
    reviewTaxPosition: () => setModel((value) => ({ ...value,
      taxPosition: { ...value.taxPosition, status: value.taxPosition.missingItems.length ? 'incomplete' : 'ready-for-review' } })),
    saveTaxPositionDraft: persistTaxPositionDraft,
    calculateTaxPosition: async (facts: PersonalTaxFactsInput) => {
      if (!individual) { setNotice('No individual tax subject is available for this identity.'); return; }
      try {
        const packageContext = individual.resolved_contexts?.find((context) => context.scope.rail === 'tax_return'
          && context.scope.packageId === facts.packageId);
        if (packageContext) api.selectContext(packageContext);
        await persistTaxPositionDraft(facts);
        const { engineFacts } = await bindPersonalTaxFacts(facts, periodStart);
        const calculation = await api.calculatePersonalTaxDraft(periodStart, periodEndExclusive);
        setTaxReturn(undefined);
        setModel((value) => withPersonalTaxCalculation(value, facts, engineFacts, calculation));
        setNotice('Personal tax estimate ready. Review the calculation trace and any remaining return requirements.');
      } catch (error) { setNotice(error instanceof Error ? error.message : 'Tax calculation failed.'); }
    },
    openReturnDraft: openOrCreateReturnDraft,
    requestProfessionalHelp: () => setNotice('Professional matching and cross-account invitations are not connected yet. If you already have a practitioner, contact them outside Amoye for now. Do not send records through Amoye until a scoped engagement is authorised. Tax Central discovery requires a separate consent-based integration.'),
  }} />;
}

function withPersonalTaxCalculation(model: TrackB2Model, facts: PersonalTaxFactsInput,
  engineFacts: Parameters<PersonaWorkspaceProps['api']['savePersonalTaxDraft']>[0]['facts'],
  calculation: TaxCalculationResult): TrackB2Model {
  const deductionLine = calculation.trace.find((line) => line.id === 'PIT-CHARGEABLE');
  const eligibleDeductionsKobo = deductionLine?.operandsKobo[1] ?? '0';
  return { ...model, taxFacts: facts,
    taxPosition: { ...model.taxPosition,
      jurisdictionLabel: facts.packageId.includes('NRS-SPECIAL') ? 'NRS special jurisdiction' : 'State or FCT jurisdiction',
      incomeLabel: `${formatKobo(engineFacts.grossIncomeKobo)} reviewed income`,
      deductionsLabel: `${formatKobo(eligibleDeductionsKobo)} eligible deductions and reliefs`,
      estimatedPositionLabel: `${formatKobo(calculation.liabilityKobo)} estimated payable`,
      effectiveRateLabel: effectivePayableRate(calculation.liabilityKobo, engineFacts.grossIncomeKobo),
      status: 'ready-for-review', missingItems: [] },
    calculation: { id: calculation.id, inputSnapshotSha256: calculation.inputSnapshotSha256,
      outputSha256: calculation.outputSha256,
      acceptance: calculation.packageAcceptance,
      lines: calculation.trace.map((line) => ({ id: line.id, rule: personalTaxTraceLabel(line),
        resultLabel: formatKobo(line.resultKobo) })),
      citations: calculation.sourceCitations.map((citation) => ({ id: citation.id, title: citation.title,
        authority: citation.authority, url: citation.url, locator: citation.relevantLocator })) },
    returnDraft: { ...model.returnDraft, id: 'personal-return-not-created', version: 'Not created',
      state: 'not-ready', declarationVersion: 'Not selected',
      blockers: ['Review the estimate, then create the return before signing'] },
    responsibilities: model.responsibilities.map((step) => ({ ...step, state: 'blocked',
      artifactVersion: 'No return created' })) };
}

function withPersonalReturn(model: TrackB2Model, taxReturn: TaxReturnResource): TrackB2Model {
  const candidateRules = model.calculation?.acceptance !== 'PRACTITIONER_ACCEPTED';
  const state = ['SIGNED', 'SUBMISSION_REQUESTED', 'MOCK_ACCEPTED'].includes(taxReturn.status)
    ? 'signed-for-mock-request' as const
    : ['PREPARED', 'REVIEWED', 'APPROVED'].includes(taxReturn.status)
      ? 'ready-for-signature' as const
      : taxReturn.status === 'CALCULATED' ? 'ready-for-review' as const : 'not-ready' as const;
  const blockers = taxReturn.status === 'DRAFT'
    ? ['Attach the reviewed figures to continue preparing this return.']
    : taxReturn.status === 'FACTS_ATTACHED'
      ? [candidateRules
          ? 'Your figures are saved. Return preparation is not available for this route yet; you can continue reviewing your tax position.'
          : 'The saved calculation still needs to be bound to this return. Select Check return status to continue.']
      : taxReturn.status === 'CALCULATED'
        ? ['The return document and declaration must be prepared before signing.'] : [];
  const artifactVersion = `Return version ${taxReturn.version}`;
  return { ...model,
    returnDraft: { id: taxReturn.id, version: `Version ${taxReturn.version}`, taxYear: model.returnDraft.taxYear,
      state, declarationVersion: state === 'ready-for-signature' || state === 'signed-for-mock-request'
        ? 'Personal declaration v1' : 'Not selected', blockers },
    responsibilities: model.responsibilities.map((step) => {
      if (step.action === 'professional-review') return { ...step, artifactVersion };
      if (step.action === 'prepare') return { ...step, artifactVersion,
        state: ['PREPARED', 'REVIEWED', 'APPROVED', 'SIGNED', 'SUBMISSION_REQUESTED', 'MOCK_ACCEPTED'].includes(taxReturn.status)
          ? 'complete' : 'blocked',
        explanation: taxReturn.status === 'FACTS_ATTACHED' && candidateRules
          ? 'Your figures are saved. Preparation will continue here when available for this return.'
          : taxReturn.status === 'CALCULATED'
            ? 'The calculated return still needs its filing document before preparation can be completed.'
            : step.explanation };
      if (step.action === 'declare-sign') return { ...step, artifactVersion,
        state: ['SIGNED', 'SUBMISSION_REQUESTED', 'MOCK_ACCEPTED'].includes(taxReturn.status) ? 'complete'
          : ['PREPARED', 'REVIEWED', 'APPROVED'].includes(taxReturn.status) ? 'ready' : 'blocked' };
      if (step.action === 'request-mock-submission') return { ...step, artifactVersion,
        state: ['SUBMISSION_REQUESTED', 'MOCK_ACCEPTED'].includes(taxReturn.status) ? 'complete' : 'blocked' };
      return { ...step, artifactVersion };
    }) };
}

function effectivePayableRate(liabilityKobo: string, grossIncomeKobo: string): string | undefined {
  const liability = BigInt(liabilityKobo); const grossIncome = BigInt(grossIncomeKobo);
  if (grossIncome === 0n) return undefined;
  const hundredthsOfPercent = (liability * 10_000n + grossIncome / 2n) / grossIncome;
  return `${hundredthsOfPercent / 100n}.${(hundredthsOfPercent % 100n).toString().padStart(2, '0')}%`;
}

function samePersonalTaxFacts(left: PersonalTaxFactsInput, right: PersonalTaxFactsInput): boolean {
  return Object.keys(left).every((key) => left[key as keyof PersonalTaxFactsInput]
    === right[key as keyof PersonalTaxFactsInput]);
}

function personalTaxInputFromDraft(packageId: PersonalTaxFactsInput['packageId'],
  facts: Parameters<PersonaWorkspaceProps['api']['savePersonalTaxDraft']>[0]['facts']): PersonalTaxFactsInput {
  return { packageId, grossIncomeNaira: savedKoboToNaira(facts.grossIncomeKobo),
    pensionNaira: savedKoboToNaira(facts.pensionKobo),
    nationalHousingFundNaira: savedKoboToNaira(facts.nationalHousingFundKobo),
    nationalHealthInsuranceNaira: savedKoboToNaira(facts.nationalHealthInsuranceKobo),
    ownerOccupiedMortgageInterestNaira: savedKoboToNaira(facts.ownerOccupiedMortgageInterestKobo),
    lifeInsurancePremiumNaira: savedKoboToNaira(facts.lifeInsurancePremiumKobo),
    annualRentPaidNaira: savedKoboToNaira(facts.annualRentPaidKobo),
    taxCreditsNaira: savedKoboToNaira(facts.taxCreditsKobo) };
}

function buildInitialPersonalModel(individual: PersonaWorkspaceProps['subjects'][number] | undefined,
  taxYear: number): TrackB2Model {
  const template = structuredClone(trackB2Fixture);
  const subjectName = individual?.display_name ?? 'Personal taxpayer';
  const packageId = individual?.resolved_contexts?.find((context) => context.scope.rail === 'tax_return')
    ?.scope.packageId;
  const selectedPackage = packageId === 'AMOYE-PIT-ANNUAL-STATE-CAND-001'
    ? packageId : 'AMOYE-PIT-ANNUAL-NRS-SPECIAL-CAND-001';
  return {
    ...template,
    context: { ...template.context, subjectName, workspaceName: subjectName, periodLabel: String(taxYear) },
    deadlines: filingDeadlineNoticesForSubject(individual),
    intake: template.intake.map((section) => ({ ...section,
      state: section.id === 'identity' ? 'complete' : 'not-started', sourceIds: [], missingItems: [] })),
    evidence: [],
    reconciliation: [],
    taxFacts: { packageId: selectedPackage, grossIncomeNaira: '0', pensionNaira: '0',
      nationalHousingFundNaira: '0', nationalHealthInsuranceNaira: '0',
      ownerOccupiedMortgageInterestNaira: '0', lifeInsurancePremiumNaira: '0', annualRentPaidNaira: '0',
      taxCreditsNaira: '0' },
    taxPosition: { ...template.taxPosition, taxYear: String(taxYear),
      jurisdictionLabel: selectedPackage === 'AMOYE-PIT-ANNUAL-STATE-CAND-001'
        ? 'State or FCT jurisdiction' : 'NRS special jurisdiction',
      incomeLabel: 'No reviewed income', deductionsLabel: 'No reviewed deductions',
      estimatedPositionLabel: 'Not calculated', status: 'incomplete',
      missingItems: ['Enter your income and relief amounts, then calculate your tax position'] },
    calculation: undefined,
    returnDraft: { ...template.returnDraft, id: 'personal-return-not-created', version: 'Not created',
      taxYear: String(taxYear), state: 'not-ready', declarationVersion: 'Not selected',
      blockers: ['Confirm your amounts in Tax position and calculate before preparing a return'] },
    responsibilities: template.responsibilities.map((step) => ({ ...step, state: 'waiting',
      artifactVersion: 'Not created', actorLabel: step.action === 'professional-review'
        ? 'No tax practitioner selected' : `${subjectName} · Self-service` })),
    submission: [],
    bridge: { ...template.bridge, retryAllowed: false, reconciliationRequired: false,
      detail: 'No filing request has been made for this return.' },
    intelligence: { ...template.intelligence, enabled: true, status: 'ready', insights: [], citations: [] },
  };
}

function inferMimeType(name: string): string {
  const extension = name.toLowerCase().split('.').at(-1);
  return extension === 'csv' ? 'text/csv' : extension === 'json' ? 'application/json'
    : extension === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      : extension === 'pdf' ? 'application/pdf' : ['jpg', 'jpeg'].includes(extension ?? '') ? 'image/jpeg'
        : extension === 'png' ? 'image/png' : extension === 'webp' ? 'image/webp' : 'application/octet-stream';
}

function evidenceSource(name: string): 'controlled-csv' | 'receipt' | 'bank-statement' | 'pos-export' | 'tax-record' {
  const extension = name.toLowerCase().split('.').at(-1);
  if (extension === 'pdf') return 'bank-statement';
  if (['png', 'jpg', 'jpeg', 'webp'].includes(extension ?? '')) return 'receipt';
  if (['xlsx', 'json'].includes(extension ?? '')) return 'pos-export';
  return 'tax-record';
}

export function reviewedPersonalTaxFacts(current: PersonalTaxFactsInput,
  reconciliation: TrackB2Model['reconciliation']): PersonalTaxFactsInput {
  const accepted = reconciliation.filter((item) => ['accepted-by-human', 'corrected-by-human'].includes(item.state)
    && item.amountKobo);
  const amount = (pattern: RegExp) => {
    const matching = accepted.filter((item) => pattern.test(item.ntaCode?.toUpperCase() ?? ''));
    return matching.length ? koboToNaira(matching.reduce((total, item) => total + BigInt(item.amountKobo!), 0n)) : undefined;
  };
  return { ...current,
    ...(amount(/(INCOME|REVENUE|SALES|SALARY|FREELANCE|EMPLOYMENT)/) ? { grossIncomeNaira: amount(/(INCOME|REVENUE|SALES|SALARY|FREELANCE|EMPLOYMENT)/)! } : {}),
    ...(amount(/PENSION/) ? { pensionNaira: amount(/PENSION/)! } : {}),
    ...(amount(/NHF|HOUSING[-_ ]?FUND/) ? { nationalHousingFundNaira: amount(/NHF|HOUSING[-_ ]?FUND/)! } : {}),
    ...(amount(/NHIS|HEALTH[-_ ]?INSURANCE/) ? { nationalHealthInsuranceNaira: amount(/NHIS|HEALTH[-_ ]?INSURANCE/)! } : {}),
    ...(amount(/MORTGAGE/) ? { ownerOccupiedMortgageInterestNaira: amount(/MORTGAGE/)! } : {}),
    ...(amount(/LIFE[-_ ]?INSURANCE/) ? { lifeInsurancePremiumNaira: amount(/LIFE[-_ ]?INSURANCE/)! } : {}),
    ...(amount(/RENT/) ? { annualRentPaidNaira: amount(/RENT/)! } : {}),
    ...(amount(/PAYE|WHT|TAX[-_ ]?(PAID|CREDIT|WITHHELD)/) ? { taxCreditsNaira: amount(/PAYE|WHT|TAX[-_ ]?(PAID|CREDIT|WITHHELD)/)! } : {}),
  };
}

function missingItemSatisfied(missing: string, ntaCode?: string): boolean {
  const missingLabel = missing.toUpperCase(); const code = ntaCode?.toUpperCase() ?? '';
  return (missingLabel.includes('PENSION') && code.includes('PENSION'))
    || (missingLabel.includes('PAYE') && code.includes('PAYE'));
}

function koboToNaira(value: bigint): string {
  const absolute = value < 0n ? -value : value;
  const whole = absolute / 100n; const fraction = (absolute % 100n).toString().padStart(2, '0');
  return `${value < 0n ? '-' : ''}${whole}.${fraction}`;
}

function personalSectionForDocumentCategory(category?: string): 'income' | 'deductions' | 'credits' | 'identity' | undefined {
  if (category === 'income') return 'income';
  if (category === 'deductions_and_reliefs') return 'deductions';
  if (category === 'tax_already_paid') return 'credits';
  if (category === 'identity_and_jurisdiction') return 'identity';
  return undefined;
}
